import { useRef, useState } from "react";
import { fileToCompressedBase64 } from "@/lib/image";
import { toast } from "sonner";
import { Camera, X, ImagePlus } from "lucide-react";

export default function PhotoUpload({ value = [], onChange, testid = "photo-upload" }) {
  const inputRef = useRef(null);
  const [processing, setProcessing] = useState(false);
  const [preview, setPreview] = useState(null); // { src } for lightbox

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setProcessing(true);
    try {
      const newOnes = [];
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) continue;
        const b64 = await fileToCompressedBase64(file);
        newOnes.push(b64);
      }
      onChange([...(value || []), ...newOnes]);
      toast.success(`Added ${newOnes.length} photo${newOnes.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Photo processing failed");
    } finally {
      setProcessing(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-3 mb-3">
        {value.map((src, i) => (
          <div key={i} className="relative w-20 h-20 border border-white/10 overflow-hidden group">
            <img
              src={src}
              alt={`attachment ${i + 1}`}
              className="w-full h-full object-cover cursor-pointer"
              onClick={() => setPreview({ src })}
            />
            <button
              type="button"
              data-testid={`${testid}-remove-${i}`}
              onClick={() => remove(i)}
              className="absolute top-1 right-1 w-5 h-5 bg-black/70 hover:bg-[#FF3B30] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        ))}
        <button
          type="button"
          data-testid={`${testid}-add`}
          onClick={() => inputRef.current?.click()}
          disabled={processing}
          className="w-20 h-20 border border-dashed border-white/20 hover:border-[#007AFF] hover:bg-[#007AFF]/5 flex flex-col items-center justify-center gap-1 text-[#A3A3A3] hover:text-[#007AFF] transition-all disabled:opacity-50"
        >
          {processing ? (
            <div className="w-4 h-4 border-2 border-white/20 border-t-[#007AFF] rounded-full animate-spin" />
          ) : (
            <>
              <ImagePlus className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-[9px] font-mono-tab tracking-wider uppercase">Add</span>
            </>
          )}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
      />

      {preview && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20"
            onClick={() => setPreview(null)}
          >
            <X className="w-5 h-5" />
          </button>
          <img src={preview.src} alt="preview" className="max-w-full max-h-[90vh] object-contain" />
        </div>
      )}
    </div>
  );
}

export function PhotoThumbs({ photos = [], size = 12 }) {
  const [preview, setPreview] = useState(null);
  if (!photos || photos.length === 0) return null;
  return (
    <>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {photos.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={`attachment ${i + 1}`}
            className={`w-${size} h-${size} object-cover border border-white/10 cursor-pointer hover:border-[#007AFF]`}
            style={{ width: `${size * 4}px`, height: `${size * 4}px` }}
            onClick={() => setPreview(src)}
          />
        ))}
      </div>
      {preview && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <img src={preview} alt="preview" className="max-w-full max-h-[90vh] object-contain" />
        </div>
      )}
    </>
  );
}
